import {useState, useEffect, useRef, useCallback} from "react";
import * as h3 from "h3-js";
import PropTypes from 'prop-types';

const RESOLUTION_RANGES = [
    {max: 8, resolution: 5}, // LOW_ZOOM
    {max: 10, resolution: 6}, // MEDIUM_ZOOM
    {max: 12, resolution: 7}, // HIGH_ZOOM
    {max: 14, resolution: 8}, // VERY_HIGH_ZOOM
    {max: 16, resolution: 9}, // ULTRA_HIGH_ZOOM
    {max: 18, resolution: 10}, // EXTREME_ZOOM
    {max: 20, resolution: 11}, // NANO_ZOOM
    {resolution: 12} // MICRO_ZOOM (fallback)
];

const validateCoordinates = (latitude, longitude) => {
    return (
        typeof latitude === 'number' &&
        typeof longitude === 'number' &&
        latitude >= -90 &&
        latitude <= 90 &&
        longitude >= -180 &&
        longitude <= 180
    );
};

export const useH3Operations = ({
    markers = [],
    initialHexagons = [],
    onHexagonSelect,
    autoResolution = false,
    isH3Enabled = true,
    defaultZoom = 10,
    map,
    maps
}) => {
    const [resolution, setResolution] = useState(() => {
        try {
            return initialHexagons.length > 0 ? initialHexagons[0].resolution : 5;
        } catch (error) {
            console.error('Resolution initialization error:', error);
            return 5;
        }
    });
    
    const [currentZoom, setCurrentZoom] = useState(defaultZoom);
    const [hoverHexagon, setHoverHexagon] = useState(null);
    const [selectedHexagons, setSelectedHexagons] = useState(() => {
        try {
            const initialState = {};
            initialHexagons.forEach(hexagon => {
                if (!initialState[hexagon.resolution]) {
                    initialState[hexagon.resolution] = new Set();
                }
                initialState[hexagon.resolution].add(hexagon.index);
            });
            return initialState;
        } catch (error) {
            console.error('Selected hexagons initialization error:', error);
            return {};
        }
    });
    
    const [markerToHexagonMap, setMarkerToHexagonMap] = useState({});
    const [previewHexagons, setPreviewHexagons] = useState([]);
    const previousHexagonsRef = useRef("");
    const isMountedRef = useRef(true);

    useEffect(() => {
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    useEffect(() => {
        if (!isMountedRef.current) return;
        
        try {
            setSelectedHexagons(() => {
                const newState = {};
                initialHexagons.forEach(hexagon => {
                    if (!newState[hexagon.resolution]) {
                        newState[hexagon.resolution] = new Set();
                    }
                    newState[hexagon.resolution].add(hexagon.index);
                });
                return newState;
            });
        } catch (error) {
            console.error('Error updating selected hexagons:', error);
        }
    }, [JSON.stringify(initialHexagons)]);

    const getResolutionForZoom = useCallback(
        zoom => {
            if (!autoResolution) return resolution;

            try {
                const {resolution: newResolution} = RESOLUTION_RANGES.find(
                    range => zoom <= (range.max ?? Infinity)
                ) || {resolution: 9};

                return newResolution;
            } catch (error) {
                console.error('Error getting resolution for zoom:', error);
                return resolution;
            }
        },
        [autoResolution, resolution]
    );

    const totalSelectedHexagons = Object.values(selectedHexagons).reduce(
        (total, hexagons) => total + hexagons.size,
        0
    );

    useEffect(() => {
        if (!isMountedRef.current) return;
        
        if (autoResolution) {
            setResolution(getResolutionForZoom(currentZoom));
        }
    }, [currentZoom, getResolutionForZoom, autoResolution]);

    const handleZoomChange = useCallback(() => {
        if (!isMountedRef.current || !map || !autoResolution) return;
        
        try {
            setCurrentZoom(map.getZoom());
        } catch (error) {
            console.error('Error handling zoom change:', error);
        }
    }, [map, autoResolution]);

    useEffect(() => {
        if (!map || !maps || !autoResolution || !isMountedRef.current) return;

        try {
            maps.event.addListener(map, "zoom_changed", handleZoomChange);
            return () => {
                if (isMountedRef.current) {
                    maps.event.clearListeners(map, "zoom_changed");
                }
            };
        } catch (error) {
            console.error('Error setting up zoom listener:', error);
        }
    }, [map, maps, handleZoomChange, autoResolution]);

    useEffect(() => {
        if (!isMountedRef.current || !markers.length) {
            setMarkerToHexagonMap({});
            return;
        }

        try {
            const hexagonMapping = {};
            markers.forEach(marker => {
                const {location} = marker;
                if (!location?.latitude || !location?.longitude || !validateCoordinates(location.latitude, location.longitude)) return;

                for (let res = 1; res <= 9; res++) {
                    const hexagonIndex = h3.latLngToCell(location.latitude, location.longitude, res);
                    if (!hexagonMapping[hexagonIndex]) {
                        hexagonMapping[hexagonIndex] = [];
                    }
                    hexagonMapping[hexagonIndex].push(marker);
                }
            });
            setMarkerToHexagonMap(hexagonMapping);
        } catch (error) {
            console.error('Error updating marker to hexagon map:', error);
            setMarkerToHexagonMap({});
        }
    }, [JSON.stringify(markers)]);

    useEffect(() => {
        if (!isMountedRef.current || !onHexagonSelect) return;

        try {
            const allSelectedHexagons = Object.entries(selectedHexagons).flatMap(
                ([resolution, hexagons]) =>
                    Array.from(hexagons).map(index => ({
                        resolution: parseInt(resolution),
                        index
                    }))
            );

            const currentHexagonsState = JSON.stringify(allSelectedHexagons);
            if (currentHexagonsState !== previousHexagonsRef.current) {
                previousHexagonsRef.current = currentHexagonsState;
                onHexagonSelect(allSelectedHexagons);
            }
        } catch (error) {
            console.error('Error in hexagon selection effect:', error);
        }
    }, [selectedHexagons, onHexagonSelect]);

    const toggleHexagonSelection = useCallback(
        (latitude, longitude) => {
            if (!isMountedRef.current || !isH3Enabled || !validateCoordinates(latitude, longitude)) return;

            try {
                const hexagonIndex = h3.latLngToCell(latitude, longitude, resolution);

                setSelectedHexagons(prevSelected => {
                    const newState = {...prevSelected};
                    const currentResolutionSet = new Set(prevSelected[resolution] || []);

                    if (currentResolutionSet.has(hexagonIndex)) {
                        currentResolutionSet.delete(hexagonIndex);
                        if (currentResolutionSet.size === 0) {
                            delete newState[resolution];
                        } else {
                            newState[resolution] = currentResolutionSet;
                        }
                        setHoverHexagon(null);
                    } else {
                        currentResolutionSet.add(hexagonIndex);
                        newState[resolution] = currentResolutionSet;
                    }

                    return newState;
                });
            } catch (error) {
                console.error('Error toggling hexagon selection:', error);
            }
        },
        [resolution, isH3Enabled]
    );

    const showNeighborHexagons = useCallback(
        hexagonId => {
            if (!isMountedRef.current || !hexagonId) return;

            try {
                const neighbors = h3.gridDisk(hexagonId, 1);
                const onlyNeighbors = neighbors
                    .filter(hexId => hexId !== hexagonId)
                    .filter(hexId => !selectedHexagons[resolution]?.has(hexId));

                setPreviewHexagons(onlyNeighbors);
            } catch (error) {
                console.error('Error showing neighbor hexagons:', error);
                setPreviewHexagons([]);
            }
        },
        [resolution, selectedHexagons]
    );

    const clearPreviewHexagons = useCallback(() => {
        if (!isMountedRef.current) return;
        setPreviewHexagons([]);
    }, []);

    return {
        resolution,
        setResolution,
        zoom: currentZoom,
        setZoom: setCurrentZoom,
        selectedHexagons,
        setSelectedHexagons,
        markerToHexagonMap,
        toggleHexagonSelection,
        totalSelectedHexagons,
        getResolutionForZoom,
        showNeighborHexagons,
        clearPreviewHexagons,
        previewHexagons,
        hoverHexagon,
        setHoverHexagon
    };
};

useH3Operations.propTypes = {
    markers: PropTypes.arrayOf(PropTypes.shape({
        location: PropTypes.shape({
            latitude: PropTypes.number.isRequired,
            longitude: PropTypes.number.isRequired
        })
    })),
    initialHexagons: PropTypes.arrayOf(PropTypes.shape({
        resolution: PropTypes.number.isRequired,
        index: PropTypes.string.isRequired
    })),
    onHexagonSelect: PropTypes.func,
    autoResolution: PropTypes.bool,
    isH3Enabled: PropTypes.bool,
    defaultZoom: PropTypes.number,
    map: PropTypes.object,
    maps: PropTypes.object
};

useH3Operations.defaultProps = {
    markers: [],
    initialHexagons: [],
    autoResolution: false,
    isH3Enabled: true,
    defaultZoom: 10
};
